import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { failHard } from './fail-loud';
import type { RouterAdapter, UrlState } from './router-types';

export type { RouterAdapter, UrlState } from './router-types';

/**
 * The router seam (#1801).
 *
 * A list's query state lives in the URL, and a form's dirty state has to be
 * able to stop a navigation. Both are router work, and core must stay free of
 * any concrete router — so core declares exactly TWO seams and the host injects
 * an implementation. Two, not more: everything else a page needs from a router
 * (building a link, pushing a detail route) is host-shaped and belongs to the
 * host, where the route table already lives.
 */

const RouterAdapterContext = createContext<RouterAdapter | null>(null);

export interface RouterAdapterProviderProps {
  adapter: RouterAdapter;
  children: ReactNode;
}

export function RouterAdapterProvider({ adapter, children }: RouterAdapterProviderProps) {
  const value = useMemo(() => adapter, [adapter]);
  return <RouterAdapterContext.Provider value={value}>{children}</RouterAdapterContext.Provider>;
}

/**
 * The installed adapter. Missing wiring throws EVERYWHERE — including prod —
 * because there is nothing to degrade to: a list whose URL state silently goes
 * nowhere renders as a working page that forgets every filter, which is far
 * worse than a loud failure at boot.
 */
function useRouterAdapter(): RouterAdapter {
  const adapter = useContext(RouterAdapterContext);
  if (!adapter) {
    failHard(
      'no router adapter installed — wrap the app in <RouterAdapterProvider> ' +
        '(the host supplies one; core owns no router)',
    );
  }
  return adapter;
}

export function useUrlState(): UrlState {
  return useRouterAdapter().useUrlState();
}

export function useNavigationBlocker(blocked: boolean, message?: string): void {
  useRouterAdapter().useNavigationBlocker(blocked, message);
}
